import { useGetCategories, useGetProductsList, usePostRecipes, usePutRecipesRecipeId } from '@agos/api-client/menu'
import type { LinkableProduct, Recipe } from '@agos/api-client/model'
import { apiErrorMessage } from '@agos/api-client'
import { T, centsToApiString, formatMoney, parseMoneyToCents, tt } from '@agos/shared'
import {
  BUTTON_GHOST,
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  toast,
} from '@agos/ui'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'

import {
  EMPTY_INGREDIENT,
  RecipeEditor,
  clearIngredientsRefusal,
  firstIncompleteRow,
  wouldClearIngredients,
  ingredientPayload,
  type IngredientDraft,
} from './RecipeEditor'
import { recipeCategoryOptions } from './recipe-categories'

/**
 * The ten names this form used to offer - kept **only** so nothing that imported them breaks, and no
 * longer what the select shows.
 *
 * §9.349, the owner's C8: four of them (`Boxy`, `Alkohol`, `Koktaily`, `Káva`) are not categories at
 * all, and twelve of the franchise's eighteen were missing - including `Burgre` and `Šaláty`, which
 * is where 14 of their recipes belong. The select reads `GET /categories` now.
 */
export const RECIPE_CATEGORIES = [
  'Pizza',
  'Cestoviny',
  'Bowle',
  'Boxy',
  tt('Prílohy'),
  'Dresingy',
  tt('Nápoje'),
  'Alkohol',
  'Koktaily',
  tt('Káva'),
]

const FIELD_CLASSES =
  'w-full px-3 py-2.5 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'
const LABEL_CLASSES = 'text-xs font-semibold text-muted-foreground uppercase tracking-wider'

function draftsFrom(recipe: Recipe | undefined): IngredientDraft[] {
  const existing = recipe?.ingredients?.map((ingredient) => ({
    stockItemId: ingredient.stockItemId ?? null,
    productId: ingredient.productId ?? null,
    name: ingredient.name ?? '',
    quantity: String(ingredient.quantity ?? ''),
    unit: ingredient.unit ?? 'g',
    cost: centsToApiString(parseMoneyToCents(ingredient.cost)),
  }))
  return existing?.length ? existing : [{ ...EMPTY_INGREDIENT }]
}

export function RecipeModal({
  recipe,
  onClose,
  onSaved,
}: {
  recipe?: Recipe
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(recipe?.name ?? '')
  // §9.349 - the categories the franchise actually has. An empty answer leaves the recipe's own
  // value as the only option, which is better than offering a name nobody chose.
  const { data: categoryData } = useGetCategories()
  const [category, setCategory] = useState(recipe?.category ?? '')
  const [description, setDescription] = useState(recipe?.description ?? '')
  const [servings, setServings] = useState(String(recipe?.servings ?? 1))
  const [prepTime, setPrepTime] = useState(recipe?.prepTime ? String(recipe.prepTime) : '')
  const [productId, setProductId] = useState<number | null>(recipe?.productId ?? null)
  const [ingredients, setIngredients] = useState<IngredientDraft[]>(() => draftsFrom(recipe))
  const [error, setError] = useState('')

  const { data: products } = useGetProductsList()

  const createRecipe = usePostRecipes()
  const updateRecipe = usePutRecipesRecipeId()
  const isSaving = createRecipe.isPending || updateRecipe.isPending

  const linkedProduct = (products ?? []).find((product: LinkableProduct) => product.id === productId)

  const addIngredient = () => setIngredients((rows) => [...rows, { ...EMPTY_INGREDIENT }])


  const save = () => {
    if (!name.trim()) {
      setError(tt('Zadajte názov receptu'))
      return
    }

    // FB-018: legacy filters incomplete rows out and saves anyway, so a
    // forgotten quantity silently drops an ingredient — and with it, part of
    // the dish's cost. Refuse, and say which row.
    const incompleteIndex = firstIncompleteRow(ingredients)
    if (incompleteIndex !== -1) {
      setError(
        `${tt('Ingrediencia č.')} ${incompleteIndex + 1} ${tt('nemá názov alebo množstvo.')}`,
      )
      return
    }

    // §9.363, FB-650 - no rows at all is the case the line above has nothing to say about. The
    // server refuses it with a sentence written for a developer (*pošli ?allowEmpty=1*), and that
    // sentence used to reach the screen. Refused here in the operator's words instead; the server's
    // guardrail stays exactly as it is, because it was strung after the owner lost ingredients.
    if (wouldClearIngredients(draftsFrom(recipe).length, ingredients)) {
      setError(clearIngredientsRefusal())
      return
    }

    setError('')
    const request = {
      name: name.trim(),
      category,
      description: description.trim() || undefined,
      servings: Number.parseInt(servings, 10) || 1,
      prepTime: Number.parseInt(prepTime, 10) || undefined,
      productId: productId ?? undefined,
      ingredients: ingredientPayload(ingredients),
    }

    const handlers = {
      onSuccess: () => {
        toast({ title: recipe ? tt('Recept upravený') : tt('Recept pridaný') })
        onSaved()
      },
      onError: (caught: unknown) => setError(apiErrorMessage(caught, tt('Neznáma chyba'))),
    }

    if (recipe?.id !== undefined) {
      updateRecipe.mutate({ recipeId: recipe.id, data: request }, handlers)
    } else {
      createRecipe.mutate({ data: request }, handlers)
    }
  }

  return (
    <div
      data-recipe-modal
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    >
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          {/* On `recipe?.id`, the same test line 153 saves by. A draft carrying only a name and a
            * `productId` — which is how `/products` opens a first recipe (§9.13) — is a *new* recipe,
            * and titling it *Upraviť* would tell somebody they are changing one that does not exist. */}
          <h2 className="text-lg font-bold text-foreground">
            {recipe?.id === undefined ? tt('Pridať recept') : tt('Upraviť recept')}
          </h2>
          <button
            type="button"
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            className={BUTTON_GHOST}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className={LABEL_CLASSES} htmlFor="recipe-nazov">
                  <T>Názov</T>
                </label>
                <input
                  id="recipe-nazov"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className={FIELD_CLASSES}
                  placeholder={tt('Napr. Margherita')}
                />
              </div>
              <div className="space-y-1.5">
                <label className={LABEL_CLASSES} htmlFor="recipe-kategoria">
                  <T>Kategória</T>
                </label>
                <select
                  id="recipe-kategoria"
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                  className={FIELD_CLASSES}
                >
                  {/* §9.349 - the franchise's own categories, plus whatever this recipe already
                    * says. The second half is not a nicety: a `<select>` whose value is absent from
                    * its options renders blank and then saves the blank over what somebody chose. */}
                  {recipeCategoryOptions(categoryData, category).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className={LABEL_CLASSES} htmlFor="recipe-popis-nepovinny">
                <T>Popis (nepovinný)</T>
              </label>
              <input
                id="recipe-popis-nepovinny"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className={FIELD_CLASSES}
                placeholder={tt('Krátky popis receptu...')}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className={LABEL_CLASSES} htmlFor="recipe-pocet-porcii">
                  <T>Počet porcií</T>
                </label>
                <input
                  id="recipe-pocet-porcii"
                  type="number"
                  min="1"
                  value={servings}
                  onChange={(event) => setServings(event.target.value)}
                  className={FIELD_CLASSES}
                />
              </div>
              <div className="space-y-1.5">
                <label className={LABEL_CLASSES} htmlFor="recipe-cas-pripravy-min">
                  <T>Čas prípravy (min)</T>
                </label>
                <input
                  id="recipe-cas-pripravy-min"
                  type="number"
                  min="1"
                  value={prepTime}
                  onChange={(event) => setPrepTime(event.target.value)}
                  className={FIELD_CLASSES}
                  placeholder={tt('Napr. 15')}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className={LABEL_CLASSES} htmlFor="recipe-prepojeny-produkt-predajna-cena">
                <T>Prepojený produkt (predajná cena)</T>
              </label>
              <div className="flex items-center gap-3">
                <select
                  id="recipe-prepojeny-produkt-predajna-cena"
                  value={productId ?? ''}
                  onChange={(event) =>
                    setProductId(event.target.value ? Number(event.target.value) : null)
                  }
                  className={cn(FIELD_CLASSES, 'flex-1')}
                >
                  <option value="">{tt('- Bez produktu -')}</option>
                  {(products ?? []).map((product: LinkableProduct) => (
                    <option key={product.id} value={product.id}>
                      {product.name} ({formatMoney(product.price)})
                    </option>
                  ))}
                </select>
                {linkedProduct?.price ? (
                  <span className="text-sm font-bold text-emerald-500 whitespace-nowrap">
                    {formatMoney(linkedProduct.price)}
                  </span>
                ) : null}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={LABEL_CLASSES}>
                  <T>Ingrediencie</T>
                </label>
                <button
                  type="button"
                  onClick={addIngredient}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg bg-muted text-xs font-semibold hover:bg-muted-foreground/20 transition-colors"
                >
                  <Plus className="w-3 h-3" /> <T>Pridať</T>
                </button>
              </div>

              {/* §9.221 - the same block the product form draws. Two copies of an ingredient row
                * is how the two drift, and this screen has been reported four times. No selling
                * price here: a recipe opened on its own has none to show a margin against. */}
              <RecipeEditor ingredients={ingredients} onChange={setIngredients} />
            </div>
          </div>

          {error && <p className="text-sm text-destructive mt-4">{error}</p>}

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
            >
              <T>Zrušiť</T>
            </button>
            <button
              type="button"
              onClick={save}
              disabled={isSaving}
              className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              {isSaving ? tt('Ukladám...') : recipe ? tt('Uložiť zmeny') : tt('Pridať recept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
